import React from "react";

interface Benefit {
  title: string;
  description: string;
}

interface BenefitsListProps {
  title: string;
  benefits: Benefit[];
}

/**
 * Benefits List component
 *
 * Generic benefits section component that displays a list of benefits in a grid layout
 * with hover effects. Receives all content as props for A/B testing flexibility.
 *
 * @param {string} title - Section title
 * @param {Benefit[]} benefits - Array of benefit objects with title and description
 * @returns {JSX.Element} The benefits list section component
 */
export function BenefitsList({ title, benefits }: BenefitsListProps) {
  return (
    <section className="py-24 px-10 text-center">
      <h2 className="text-4xl font-extrabold mb-12 text-slate-800">{title}</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-7xl mx-auto">
        {benefits.map((benefit, index) => (
          <div
            key={index}
            className="p-10 rounded-2xl bg-[#F9FAFB] text-left border-b-4 border-transparent transition-all duration-300 hover:border-[#8B63F1] hover:bg-white hover:shadow-[0_10px_30px_rgba(0,0,0,0.05)]"
          >
            <h3 className="font-bold text-lg mb-3 text-slate-800">
              {benefit.title}
            </h3>
            <p
              className="text-slate-600 text-sm leading-relaxed"
              dangerouslySetInnerHTML={{ __html: benefit.description }}
            />
          </div>
        ))}
      </div>
    </section>
  );
}
